import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const { isLoaded, isSignedIn, getToken, userId } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const syncUser = async () => {
      if (!isLoaded) return;
      
      if (!isSignedIn) {
        setUser(null);
        setLoading(false);
        return;
      }

      try {
        if (typeof getToken !== 'function') {
          console.error('Clerk auth is loaded but getToken is unavailable.');
          api.setToken(null);
          setUser(null);
          return;
        }

        const token = await getToken();
        if (token) {
          api.setToken(token);
          const userData = await api.getCurrentUser();
          setUser(userData);
        } else {
          api.setToken(null);
          setUser(null);
        }
      } catch (error) {
        console.error('Error syncing user:', error);
        api.setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    syncUser();
  }, [isLoaded, isSignedIn, getToken, userId]);

  const value = {
    user,
    loading,
    isSignedIn,
    isAdmin: user?.role === 'admin',
    isOpsManager: user?.role === 'ops_manager' || user?.role === 'admin',
    isCountryManager: user?.role === 'country_manager',
    isVendor: user?.role === 'vendor',
    userCountries: user?.country_codes || [],
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAppAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAppAuth must be used within AuthProvider');
  }
  return context;
}
